import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { millisecondsToTimestamp } from 'Utils/text';
import { TemplateVersion } from 'Utils/templates';
import 'Components/vm-video-card/vm-video-card';
import Styles from './vm-video-card-related.scss';
import { Contributor } from 'Components/vm-video-card/vm-video-card';

/**
 * Thin wrapper around the presentational `<vm-video-card>` for the related-video
 * rail. `vm-video-card` is a generic, framework-agnostic layout that takes
 * structured props (`heading`, a `{name, href}` publisher object, a
 * `{name, href}[]` contributors array, a pre-formatted `duration-label`, etc.).
 *
 * Most props map straight through under `vm-video-card`'s own names (`href`,
 * `heading`, `thumbnail-src`, `views-count`, `created-at`, `publisher`,
 * `contributors`). The one transformation this component owns is the duration:
 *  - `duration` (raw ms) → `duration-label` (formatted "MM:SS")
 *
 * Related cards link the whole card to the video (via `href`) but carry no
 * secondary links: callers pass `publisher`/`contributors` as plain
 * `{name}` objects (no `href`), so they render as plain text.
 *
 * @attr href          - Resolved video URL (e.g. from the `related_video_url` tag).
 * @attr heading       - Video title (shares `vm-video-card`'s `heading` prop,
 *                        named to avoid shadowing the global HTML `title` attr).
 * @attr thumbnail-src - Thumbnail image URL; empty shows the card placeholder.
 * @attr duration      - Raw video duration in milliseconds; formatted to "MM:SS".
 * @attr views-count   - Raw view count.
 * @attr created-at    - ISO-8601 publish timestamp. Omit to hide the date.
 * @prop publisher     - `{name, href?}` publisher object, forwarded as-is.
 * @prop contributors  - `{name, href?}[]` contributors, forwarded as-is.
 * @attr template-version - Markup template to render; forwarded to `vm-video-card`.
 */
@customElement('vm-video-card-related')
export class VmVideoCardRelated extends LitElement {
    static override styles = Styles;

    @property({ type: String }) href = '';
    @property({ type: String }) heading = '';
    @property({ type: String, attribute: 'thumbnail-src' }) thumbnailSrc = '';
    @property({ type: Number }) duration = 0;
    @property({ type: Number, attribute: 'views-count' }) viewsCount = 0;
    @property({ type: String, attribute: 'created-at' }) createdAt = '';
    @property({ type: Array }) contributors: Array<Contributor> = [];
    @property({ type: Object }) publisher: Contributor | null = null;
    @property({ type: String, attribute: 'template-version' })
    templateVersion: TemplateVersion = TemplateVersion.Latest;

    private get _durationLabel(): string {
        return this.duration > 0 ? millisecondsToTimestamp(this.duration) : '';
    }

    override render() {
        return html`
            <vm-video-card
                href=${this.href}
                heading=${this.heading}
                thumbnail-src=${this.thumbnailSrc}
                duration-label=${this._durationLabel}
                views-count=${this.viewsCount}
                created-at=${this.createdAt}
                template-version=${this.templateVersion}
                .publisher=${this.publisher}
                .contributors=${this.contributors}
            ></vm-video-card>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-card-related': VmVideoCardRelated;
    }
}
